import React from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Checkbox,
  CheckboxGroup,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { TipoAlteracao } from '../../../../../hooks/useSocietario';
import { IWizardFormValues, TIPO_ALTERACAO_OPTIONS, wizardDefaults } from '../schema';
import { classificarRotaAlteracao } from '../classificarRotaAlteracao';

// Sessao 2026-05-11 (item 3 Fase 1): tipos que adicionam steps condicionais
// ao wizard. Quando marcados, mostramos mensagem reativa explicando.
const TIPOS_QUE_ADICIONAM_SOCIOS = [
  'quadro_societario',
  'dados_socio',
  'administracao',
  'capital',
] as const;

// Tipos NAO cobertos pelo motor deterministico (v3) — espelha `TIPOS_FORA_DE_DE`
// em derivarPatchesV3.ts. Marcar qualquer um faz `cobertoEmDE=false` -> TODA a
// alteracao e roteada pro v2 (IA generativa), menos fiel ao vigente. Sinalizamos.
// Wave 2 (2026-06-22): 'administracao' SAIU daqui — agora e deterministico no v3
// (saiu de TIPOS_FORA_DE_DE), entao NAO deve mais disparar o aviso de IA.
const TIPOS_PROCESSADOS_POR_IA = ['outras'] as const;

// O "novo" nasce vazio ao marcar o tipo: com o valor atual no campo, marcar e nao
// editar gerava alteracao com o mesmo valor, e trocar so' os CNAEs mandava o objeto
// antigo como novo (varredura de 25/09). O atual aparece em leitura no passo Empresa;
// desmarcar restaura o atual (resetTipoParaVigente, index.tsx). So' no clique: a
// retomada de rascunho nao passa por aqui e mantem o que foi digitado.
const CAMPOS_DO_VALOR_NOVO: Partial<Record<TipoAlteracao, (keyof IWizardFormValues)[]>> = {
  razao_social: ['razao_social'],
  nome_fantasia: ['nome_fantasia'],
  endereco: ['endereco'],
  objeto_social: ['objeto_social'],
};

/**
 * Step 2 do wizard de **alteracao**: multi-select dos tipos.
 *
 * Backend (Fase 4 Slice A) exige >=1 item. Cada tipo marcado:
 * - guia o prompt da IA (foca a alteracao so nos itens declarados)
 * - influencia quais sub-steps subsequentes aparecem (ex: `quadro_societario`
 *   habilita o step de Socios)
 * - filtra o validator V_ALTERACAO_PRESERVA_NAO_ALTERADO (Fase 5B) — campos
 *   nao marcados que divergem do contrato vigente viram sugestao
 *
 * `outras` e wildcard textual: desabilita o validator de preservacao.
 */
interface TiposAlteracaoStepProps {
  /** Só no fluxo da Legalização o gate "cadastro confere" (2ª trava da junta) aparece
   *  e é obrigatório; em alteração avulsa (módulo Contratos) não trava. */
  ehFluxoLegalizacao?: boolean;
}

export const TiposAlteracaoStep = ({ ehFluxoLegalizacao = false }: TiposAlteracaoStepProps) => {
  const { control, setValue } = useFormContext<IWizardFormValues>();

  // Watched pra reagir a marcacao do operador.
  const tiposSelecionados = useWatch({ control, name: 'tipos_alteracao' }) ?? [];
  const adicionouSocios = tiposSelecionados.some(t =>
    (TIPOS_QUE_ADICIONAM_SOCIOS as readonly string[]).includes(t),
  );
  let motivoSocios: string | null = null;
  if (tiposSelecionados.includes('quadro_societario')) {
    motivoSocios = '"Quadro societário"';
  } else if (tiposSelecionados.includes('dados_socio')) {
    motivoSocios = '"Dados de sócio"';
  } else if (tiposSelecionados.includes('administracao')) {
    motivoSocios = '"Administração"';
  } else if (tiposSelecionados.includes('capital')) {
    motivoSocios = '"Capital social"';
  }

  // Tipos marcados que forçam o roteamento pra IA (v2). Mapeia pros labels.
  const tiposIaSelecionados = (TIPOS_PROCESSADOS_POR_IA as readonly string[])
    .filter(t => tiposSelecionados.includes(t as TipoAlteracao))
    .map(t => TIPO_ALTERACAO_OPTIONS.find(o => o.value === t)?.label ?? t);

  // Fase 0 (legalização) — ROTA A/B derivada dos tipos marcados (com/sem viabilidade).
  const rota = classificarRotaAlteracao(tiposSelecionados);

  return (
    <Box>
      <Text fontSize="lg" fontWeight="bold" mb={1}>
        O que está sendo alterado?
      </Text>
      <Text fontSize="sm" color="gray.600" mb={4}>
        Marque ao menos 1 item. Os steps seguintes aparecem conforme o que você marcar (ex: marcar
        &quot;Quadro societário&quot; habilita o step de Sócios).
      </Text>

      <Alert status="info" borderRadius="md" mb={4} fontSize="sm">
        <AlertIcon />
        <AlertDescription>
          Os campos não marcados aqui permanecem iguais ao contrato vigente — a geração foca apenas
          nos itens declarados.
        </AlertDescription>
      </Alert>

      <Controller
        control={control}
        name="tipos_alteracao"
        render={({ field, fieldState }) => (
          <Box>
            <CheckboxGroup
              colorScheme="purple"
              value={field.value ?? []}
              onChange={(next: TipoAlteracao[]) => {
                const antes = (field.value ?? []) as TipoAlteracao[];
                next
                  .filter(t => !antes.includes(t))
                  .forEach(t =>
                    (CAMPOS_DO_VALOR_NOVO[t] ?? []).forEach(campo =>
                      setValue(campo, '' as never, { shouldDirty: true }),
                    ),
                  );
                // A sede nova mora nos 7 campos: também nasce vazia.
                if (next.includes('endereco') && !antes.includes('endereco')) {
                  setValue(
                    'endereco_estruturado',
                    { ...wizardDefaults.endereco_estruturado },
                    { shouldDirty: true },
                  );
                }
                field.onChange(next);
              }}
            >
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                {TIPO_ALTERACAO_OPTIONS.filter(opt => !opt.oculto).map(opt => (
                  <Box
                    key={opt.value}
                    p={3}
                    borderRadius="md"
                    borderWidth={1}
                    borderColor={field.value?.includes(opt.value) ? 'purple.300' : 'gray.200'}
                    bg={field.value?.includes(opt.value) ? 'purple.50' : 'white'}
                    _hover={{ borderColor: 'purple.300' }}
                    cursor="pointer"
                  >
                    <Checkbox value={opt.value} alignItems="flex-start">
                      <Stack spacing={0} ml={1}>
                        <Text fontWeight="semibold" fontSize="sm">
                          {opt.label}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          {opt.descricao}
                        </Text>
                      </Stack>
                    </Checkbox>
                  </Box>
                ))}
              </SimpleGrid>
            </CheckboxGroup>
            {fieldState.error?.message && (
              <Text color="red.500" fontSize="sm" mt={2}>
                {fieldState.error.message}
              </Text>
            )}
          </Box>
        )}
      />

      {adicionouSocios && motivoSocios && (
        <Alert status="success" borderRadius="md" mt={4} fontSize="sm">
          <AlertIcon />
          <AlertDescription>
            Adicionamos o passo de <strong>Sócios</strong> porque você marcou {motivoSocios}.
          </AlertDescription>
        </Alert>
      )}

      {tiposIaSelecionados.length > 0 && (
        <Alert status="warning" borderRadius="md" mt={4} fontSize="sm" alignItems="flex-start">
          <AlertIcon />
          <AlertDescription>
            {tiposIaSelecionados.length === 1 ? 'O tipo ' : 'Os tipos '}
            <strong>{tiposIaSelecionados.join(' e ')}</strong>{' '}
            {tiposIaSelecionados.length === 1 ? 'é processado' : 'são processados'} pela{' '}
            <strong>IA</strong> (motor legado), menos fiel ao texto do contrato vigente. Ao marcar
            {tiposIaSelecionados.length === 1 ? ' esse tipo' : ' um desses tipos'},{' '}
            <strong>toda a alteração</strong> passa pela IA — inclusive os demais itens marcados
            aqui. Revise o documento gerado com atenção antes de registrar na Junta.
          </AlertDescription>
        </Alert>
      )}

      {/* Fase 0 (legalização) — ROTA A/B derivada dos tipos marcados. */}
      {rota.rota && (
        <Alert
          status={rota.revisar ? 'warning' : 'info'}
          borderRadius="md"
          mt={4}
          fontSize="sm"
          alignItems="flex-start"
        >
          <AlertIcon />
          <AlertDescription>
            <strong>
              Rota {rota.rota} — {rota.rota === 'A' ? 'COM viabilidade' : 'SEM viabilidade'}
            </strong>{' '}
            (
            {rota.rota === 'A'
              ? 'consulta prévia na junta/prefeitura → protocolo de viabilidade'
              : 'transmite direto no REDESIM'}
            ). {rota.motivo}
          </AlertDescription>
        </Alert>
      )}

      {/* Fase 0 (legalização) — gate "cadastro bate com o último contrato?" (2ª trava
          do guia). SÓ no fluxo da Legalização; alteração avulsa (Contratos) NÃO trava
          (a Legalização já enforce isso pelo checklist `cadastro_bate` do drawer). */}
      {ehFluxoLegalizacao && (
        <Controller
          control={control}
          name="cadastro_confere"
          render={({ field, fieldState }) => (
            <Box
              mt={4}
              p={3}
              borderWidth={1}
              borderRadius="md"
              borderColor={fieldState.error ? 'red.300' : 'gray.200'}
              bg={field.value ? 'green.50' : undefined}
            >
              <Checkbox
                isChecked={!!field.value}
                onChange={e => field.onChange(e.target.checked)}
                alignItems="flex-start"
              >
                <Stack spacing={0} ml={1}>
                  <Text fontSize="sm" fontWeight="semibold">
                    Confiro que o cadastro na junta bate com o último contrato registrado
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    Antes de alterar, confira nome, endereço, capital e quadro de sócios do cadastro
                    contra o último contrato arquivado — divergências travam o registro na junta.
                  </Text>
                </Stack>
              </Checkbox>
              {fieldState.error?.message && (
                <Text color="red.500" fontSize="xs" mt={2}>
                  {fieldState.error.message}
                </Text>
              )}
            </Box>
          )}
        />
      )}
    </Box>
  );
};

export default TiposAlteracaoStep;
